/**
 * AskGenie Component - AI-Powered Contract Intelligence Chatbot
 * 
 * Provides an interactive floating chat panel for users to query contract data using AI.
 * Features include:
 * - Real-time markdown message rendering with formatting (bold, italic, lists)
 * - Popular preset queries for quick analysis (expired contracts, cashflow, vendor risk, key clauses)
 * - Touch/swipe gesture support (swipe right to expand, swipe left to close)
 * - Responsive layout that adapts to screen size
 * - Integration with backend AskGenie API via getAskGenieResults service
 * - Page access control and denied access handling
 * - Auto-scroll to latest messages and typing indicators
 * - Persistent floating panel with expandable/collapsible state
 * 
 * @component
 * @returns {JSX.Element} Floating chat panel interface with welcome screen and message area
 * 
 * @example
 * import { AskGenie } from './pages/contractgenie';
 * <AskGenie />
 * 
 * @requires React, React Hooks (useState, useEffect, useRef, useCallback)
 * @requires services/admin - getAskGenieResults API service
 * @requires utils/config_mapping - API success codes
 * @requires utils/page_access - Access control utilities
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {getAskGenieResults} from "../../services/admin"
import CommonIcon from './customicon';
import {API_SUCCESS_RESPONSE_CODE,API_SUCCESS_RESPONSE_CREATED_CODE} from "../../utils/config_mapping"
import { hasPageAccess,accessDenied,AccessDeniedModal } from "../../utils/page_access"; // Import page access control
import HeaderLogo  from "../structure/headerlogo";

export const AskGenie = () => {

  // Page name for access control verification
  let pageName = "Ask Genie";

  // ========== STATE MANAGEMENT ==========
  // Panel visibility and expansion state
  const [isOpen, setIsOpen] = useState(false); // Controls if chat panel is visible
  const [isExpanded, setIsExpanded] = useState(false); // Controls if panel is in expanded/fullscreen mode
  const [isInitialized, setIsInitialized] = useState(false); // Flag to track if component initialization is complete
  
  // Chat message management
  const [messages, setMessages] = useState([]); // Array of message objects {id, text, sender: 'user'|'assistant'}
  const [inputValue, setInputValue] = useState(""); // Current text in message input field
  
  // UI state indicators
  const [showWelcome, setShowWelcome] = useState(true); // Shows welcome screen when no messages exist
  const [isTyping, setIsTyping] = useState(false); // Indicates if AI response is being processed
  
  // Responsive layout
  const [panelWidth, setPanelWidth] = useState('80%'); // Dynamic width based on .page-content element size
  

  // ========== DOM REFERENCES (useRef) ==========
  // Panel and button references for DOM manipulation and event handling
  const panelRef = useRef(null); // Reference to main chat panel container
  const buttonRef = useRef(null); // Reference to chat button/trigger
  const inputRef = useRef(null); // Reference to message input textarea
  const messagesEndRef = useRef(null); // Reference to messages end marker for auto-scroll
  
  // Touch event tracking for swipe gesture detection
  const touchStartX = useRef(0); // X-coordinate of touch start position
  const touchStartY = useRef(0); // Y-coordinate of touch start position



  // ========== POPULAR QUERIES DATA ==========
  // Array of preset AI queries that users can click to quickly ask common questions
  // Each query includes an icon, title, description, and the actual query string
  const popularQueries = [
    {
      icon: "📅",
      title: "Contracts Expired",
      description: "View all contracts that have expired.",
      query: "List all expired contracts"
    },
    {
      icon: "💲",
      title: "Monthly Cashflow Summary",
      description: "Monitor financial obligations.",
      query: "What's the total cashflow from active contracts this month?"
    },
    {
      icon: "🛡️",
      title: "Vendor Risk Assessment",
      description: "Identify potential risks in vendor agreements.",
      query: "Which vendors have high-risk contracts?"
    },
    {
      icon: "📑",
      title: "Key Clause Summary",
      description: "Review critical terms across contracts.",
      query: "Show key clauses for all active contracts"
    }
  ];

  
  // ========== EVENT LISTENERS ==========
  /**
   * Listener for sidebar menu clicks
   * Recalculates panel width when menu is clicked to accommodate layout changes
   * Adds small delay to allow layout to settle before recalculating
   */
  useEffect(() => {
    const handleMenuClick = () => {
      // Allow layout to settle before recalculating panel width
      setTimeout(() => {
       getPanelWidth()
      }, 100);
    };
  
    // Attach listener to menu click element
    const menuClickElement = document.getElementById('menuclick');
    if (menuClickElement) {
      menuClickElement.addEventListener('click', handleMenuClick);
      
      // Cleanup: remove listener on component unmount
      return () => {
        menuClickElement.removeEventListener('click', handleMenuClick);
      };
    }
  }); // Add dependencies as needed
  

  // ========== MARKDOWN PARSING ==========
  /**
   * Converts markdown formatted text to HTML
   * Handles bold, italic, numbered lists, bullet points, and paragraph breaks
   * 
   * @param {string} text - Markdown text to parse
   * @returns {string} HTML formatted text with className attributes for styling
   * 
   * @example
   * parseMarkdown("**bold** and *italic* text")
   * // Returns: "<strong>bold</strong> and <em>italic</em> text"
   */
  const parseMarkdown = (text) => {
    return text
      // Convert bold text **text** or __text__ to <strong> tags
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      // Convert italic text *text* or _text_ to <em> tags
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      // Format numbered lists (e.g., "1. item") with numbered-item class for styling
      .replace(/^(\d+\.\s+)(.*)$/gm, '<div class="numbered-item">$1$2</div>')
      // Format bullet points with • to bullet-item class for consistent spacing
      .replace(/^•\s+(.*)$/gm, '<div class="bullet-item">• $1</div>')
      // Handle paragraph breaks - convert double line breaks to paragraph-break divs
      .replace(/\n\n+/g, '<div class="paragraph-break"></div>')
      // Convert single line breaks to spaces (avoid <br> for every newline)
      .replace(/\n/g, ' ');
  };

  // ========== API DATA FETCHING ==========
  /**
   * Fetches AI response from AskGenie backend API
   * Calls getAskGenieResults with the user query and retrieves AI-generated analysis
   * Extracts and formats additional metadata about the analysis results
   * 
   * @async
   * @param {string} query - User's natural language query
   * @returns {Promise<string>} Formatted response text with answer and additional analysis metadata
   * 
   * Response includes:
   * - Main AI-generated answer
   * - Analysis Summary: contracts found, entities analyzed, content chunks, relationships
   * - Key Entities: top 5 entities mentioned in the analysis
   * - Retrieval method used
   * 
   * @example
   * const response = await fetchData("List expired contracts");
   */
  const fetchData = async (query) => {
    try {
      // Build API request with query and graph analysis flag
      const requestPayload = {
        query: query,
        use_graph: true // Enable knowledge graph analysis for deeper insights
      };
      
      console.log("Sending request to AskGenie:", requestPayload);
      
      // Call the AskGenie API service
      const result = await getAskGenieResults(requestPayload);
      
      // Check if API call was successful
      if (result.code === API_SUCCESS_RESPONSE_CODE) {
        console.log("AskGenie response:", result);
        
        // Build response message from API data
        let responseText = "";
        
        if (result.success && result.data) {
          // Extract main answer from API response
          responseText = result.data.answer || "No answer provided.";
          
          // Add additional analysis information if available
          if (result.data.additional_info) {
            const additionalInfo = result.data.additional_info;
            responseText += "\n\n📊 **Analysis Summary:**";
            
            // Add contract count
            if (additionalInfo.contracts_found !== undefined) {
              responseText += `\n• Contracts Found: ${additionalInfo.contracts_found}`;
            }
            
            // Add entity count analyzed
            if (additionalInfo.entity_count) {
              responseText += `\n• Entities Analyzed: ${additionalInfo.entity_count}`;
            }
            
            // Add content chunks processed
            if (additionalInfo.content_chunks_count) {
              responseText += `\n• Content Chunks: ${additionalInfo.content_chunks_count}`;
            }
            
            // Add relationship count from knowledge graph
            if (additionalInfo.relationship_count) {
              responseText += `\n• Relationships: ${additionalInfo.relationship_count}`;
            }
            
            // Add retrieval method information
            if (result.data.retrieval_method) {
              responseText += `\n• Method: ${result.data.retrieval_method}`;
            }
            
            // Extract and display top 5 primary entities with formatting
            if (additionalInfo.primary_entities && additionalInfo.primary_entities.length > 0) {
              responseText += `\n\n🔍 **Key Entities:**`;
              additionalInfo.primary_entities.slice(0, 5).forEach(entity => {
                // Format entity name by replacing underscores with spaces and capitalizing words
                responseText += `\n• ${entity.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`;
              });
            }
          }
        } else {
          responseText = "I received your query but couldn't generate a proper response. Please try again.";
        }
        
        return responseText;
      } else {
        // Handle unexpected API response
        console.warn("Unexpected AskGenie response:", result);
        return "I'm sorry, I couldn't process your request at the moment. Please try again.";
      }
    } catch (error) {
      // Handle API call errors
      console.error("Error fetching AskGenie results:", error);
      return "I encountered an error while processing your request. Please try again later.";
    }
  };

  // ========== MESSAGE RENDERER COMPONENT ==========
  /**
   * Renders markdown-formatted message content as HTML
   * Converts parsed markdown to HTML using dangerouslySetInnerHTML
   * Uses pre-wrap to preserve whitespace formatting
   * 
   * @component
   * @param {Object} props - Component props
   * @param {string} props.text - Message text containing markdown formatting
   * @returns {JSX.Element} Div with parsed HTML content
   */
  const MessageRenderer = ({ text }) => {
    // Parse markdown to HTML format
    const parsedContent = parseMarkdown(text);
    
    return (
      <div 
        dangerouslySetInnerHTML={{ __html: parsedContent }}
        style={{ whiteSpace: 'pre-wrap' }}
      />
    );
  };

  // ========== AUTO-SCROLL TO LATEST MESSAGE ==========
  /**
   * Automatically scrolls to the bottom of messages when new messages arrive or typing indicator changes
   * Provides smooth scrolling behavior for better UX
   */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // ========== WELCOME SCREEN TOGGLE ==========
  /**
   * Shows/hides welcome screen based on message history
   * Displays welcome screen only when there are no messages
   * Hides when user starts chatting
   */
  useEffect(() => {
    if (messages.length > 0) {
      setShowWelcome(false);
    } else {
      setShowWelcome(true);
    }
  }, [messages]);


  // ========== TOUCH/SWIPE GESTURE HANDLERS ==========
  /**
   * Records touch start position for swipe gesture detection
   * Captures both X and Y coordinates to distinguish horizontal vs vertical swipes
   * 
   * @param {TouchEvent} e - React touch event
   */
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  /**
   * Handles touch end and determines swipe gesture direction
   * Swipe right (deltaX > 0) expands the panel if not already expanded
   * Swipe left (deltaX < 0) closes the panel if it's open
   * Ignores vertical movements (only responds to horizontal swipes > 50px)
   * 
   * @param {TouchEvent} e - React touch event
   */
  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const deltaX = touchEndX - touchStartX.current;
    const deltaY = touchEndY - touchStartY.current;

    // Only handle horizontal swipes (50px+ threshold to avoid accidental triggers)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX > 0 && !isExpanded) {
        // Swipe right to expand panel
        handleExpandToggle();
      } else if (deltaX < 0 && isOpen) {
        // Swipe left to close panel
        handleClose();
      }
    }
  };

  // ========== MESSAGE SUBMISSION ==========
  /**
   * Handles user message submission and AI response generation
   * Adds user message to chat, calls AI API for response, displays AI message
   * Manages typing indicator and error states
   * 
   * @async
   * @param {string} [messageText=null] - Optional message text; uses inputValue if not provided
   * 
   * Flow:
   * 1. Validate message and check typing state
   * 2. Add user message to messages array
   * 3. Clear input field
   * 4. Show typing indicator
   * 5. Fetch AI response via fetchData()
   * 6. Add assistant response to messages
   * 7. Hide typing indicator
   * 8. Handle errors with error message response
   */
  const handleSendMessage = async (messageText = null) => {
    // Get message text from parameter or input field
    const textToSend = messageText || inputValue.trim();
    
    // Validate message is not empty and not currently waiting for response
    if (textToSend && !isTyping) {
      // Create user message object with unique ID and timestamp
      const newMessage = {
        id: Date.now(),
        text: textToSend,
        sender: "user"
      };
      
      // Add user message to message history
      setMessages(prev => [...prev, newMessage]);
      setInputValue(""); // Clear input field
      
      // Show typing indicator to user
      setIsTyping(true);
      
      try {
        // Fetch AI response from AskGenie API
        const responseText = await fetchData(textToSend);
        const responseId = Date.now() + 1;
        
        // Create assistant response object
        const response = {
          id: responseId,
          text: responseText,
          sender: "assistant"
        };
        
        // Add assistant response to message history
        setMessages(prev => [...prev, response]);
        setIsTyping(false); // Hide typing indicator
        
      } catch (error) {
        console.error("Error in handleSendMessage:", error);
        
        // Create error response message
        const errorResponse = {
          id: Date.now() + 1,
          text: "I'm sorry, I encountered an error while processing your request. Please try again.",
          sender: "assistant"
        };
        
        // Add error message and clear typing state
        setMessages(prev => [...prev, errorResponse]);
        setIsTyping(false);
      }
    }
  };

  // ========== KEYBOARD INPUT HANDLING ==========
  /**
   * Handles keyboard events in message input field
   * Submits message on Enter key (but not Shift+Enter which adds newline)
   * Ignores submissions when AI is processing response
   * 
   * @param {KeyboardEvent} e - React keyboard event
   */
  const handleKeyPress = (e) => {
    // Submit on Enter key press, but allow Shift+Enter for multiline
    if (e.key === 'Enter' && !e.shiftKey && !isTyping) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ========== POPULAR QUERY HANDLER ==========
  /**
   * Handles clicks on popular query suggestion cards
   * Sets input value and auto-submits the preset query
   * Prevents submission if AI is currently processing
   * 
   * @param {string} query - The preset query text to submit
   */
  const handlePopularQueryClick = (query) => {
    // Only allow if not currently waiting for response
    if (!isTyping) {
      setInputValue(query); // Set input to selected query
      // Auto-send the query immediately
      handleSendMessage(query);
    }
  };

  // ========== RESPONSIVE PANEL WIDTH CALCULATION ==========
  /**
   * Calculates dynamic panel width based on .page-content element
   * Adjusts panel to fit available screen space with minimum constraint
   * Uses useCallback to prevent unnecessary re-renders
   * 
   * Width calculation:
   * - Gets width of .page-content container
   * - Leaves 10px margin on right side
   * - Maintains minimum width of 320px for mobile usability
   * 
   * @callback
   * @returns {void} Updates panelWidth state
   */
  const getPanelWidth = useCallback(() => {
    // Get the page content container element
    const pageEl = document.querySelector('.page-content');
    if (pageEl) {
      const pageRect = pageEl.getBoundingClientRect();
      // Use clientWidth for stable integer content width (excludes scrollbars)
      const pageClientWidth = pageEl.clientWidth;
      const pageWidth = Math.round(pageClientWidth);
      console.log("pageEl rect width:", Math.round(pageRect.width), "clientWidth:", pageClientWidth, "using:", pageWidth);

      // Calculate desired width with viewport constraints
      const viewportWidth = window.innerWidth;
      const desiredWidth = Math.round(Math.min(pageWidth, viewportWidth)); // Account for margins
      const finalWidth = Math.max(320, desiredWidth); // Ensure minimum width for mobile
      
      // Set panel width with right margin
      setPanelWidth(`${pageWidth - 10}px`);
    }
  }, []);

  // ========== INITIALIZE PANEL WIDTH ==========
  /**
   * Calls getPanelWidth on component mount and when dependencies change
   * Ensures panel width is set correctly when component first loads
   */
  useEffect(() => {
    getPanelWidth();
  }, [getPanelWidth]);

  // ========== RENDER MAIN COMPONENT ==========
  /**
   * Main component return with:
   * - Page header with "Ask Genie" title
   * - Chat panel container (fixed positioned)
   * - Welcome screen with popular queries
   * - Messages display area with markdown rendering
   * - Input area with send button
   * - Access control checking
   * - Custom CSS for animations and styling
   */
  return (
    <>
      {/* Main content wrapper */}
<div className="main-content" style={{ paddingTop: 64 }}>
       <HeaderLogo />
        {/* Page content container */}
        <div className="page-content">
            {/* Bootstrap fluid container for responsive layout */}
            <div className="container-fluid">
           <>

           {/* Page Header Section */}
 <div className="mb-4">
            {/* Header flex layout with icon, title, and logo */}
            <div className="d-flex align-items-center mb-2">
              {/* Ask Genie icon with primary background */}
              <div className="bg-primary text-white rounded p-2 me-3">
                <CommonIcon name="WandSparkles" size={"24px"}  color="#fff" />
              </div>
              
              {/* Title and subtitle section */}
              <div className="flex-grow-1">
                <h1 className="h3 mb-0 fw-bold"> &nbsp;Ask Genie</h1>
                <p className="text-muted mb-0 small">Ask Genie uses AI to help you find answers.</p>
              </div>
              
              {/* Predatica logo in top right */}
     <div className="ms-auto bg-primary" style={{padding:"1px",border:"1px solid #000",position:"absolute", right:"5px",borderRadius:"2px",top:"15px"}}>
    <img src="../images/predatica_logo.png" alt="Logo" height={"20px"}  /> 
    </div>
            </div>  

          </div>

          {/* Error message container */}
                <div id="app_err"></div>

      {/* ========== CHAT PANEL CONTAINER ========== */}
      {/* Check page access - show chat panel if user has view access, otherwise show access denied */}
      {hasPageAccess(pageName, "view") === false ? accessDenied() : 
          (
          <>  
      {/* Fixed position floating chat panel */}
      {/* Fixed positioning to overlay page content */}
      <div
        ref={panelRef}
        id="floating-chat-panel"
        style={{
            position: 'fixed',
            top: '100px',
            right: 10,
            bottom: 0,
             width: panelWidth,
            display: 'flex',
            flexDirection: 'column',
            background: '#fff',
            zIndex: 1050, // High z-index to overlay above content
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chat-panel-title"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >

        {/* ========== MESSAGES/CONTENT AREA ========== */}
        {/* Scrollable container for welcome screen or messages */}
        {/* Flex container with scroll and padding below input */}
        <div className="flex-grow-1 overflow-auto"
        style={{ 
    flex: 1,
    overflowY: 'auto',
    paddingBottom: '100px' // spacing above input area
  }}
        
    >
            
          {/* ========== WELCOME SCREEN ========== */}
          {showWelcome ? (
            // Show welcome screen when no messages exist
            <div className="d-flex flex-column justify-content-center align-items-center text-center" >
              <div className="mb-4">
                <div className="mb-3 mt-2">
                  {/* Spacing element */}
                  &nbsp;
                </div>
                {/* Welcome heading and instructions */}
                  <>
                    <h4 className={`fw-bold text-primary mb-2`} >Welcome to Contract Assistance</h4>
                    <p className={`text-muted mb-4 ${!isExpanded ? 'small' : ''}`}>Get started with these popular queries or ask your own question.</p>
                  </>
              </div>

              {/* ========== POPULAR QUERIES SECTION ========== */}
              {/* Grid of preset query cards for quick access */}
              <div  style={{ maxWidth: isExpanded ? '90%' : '90%', transition: 'max-width 0.3s ease' }}>
                <div className="row g-3">
                  {/* Render each popular query as an interactive card */}
                  {popularQueries.map((query, index) => (
                    <div className={`${isExpanded ? 'col-md-6' : 'col-md-6'}`} key={index}>
                      {/* Query card with hover effects */}
                      <div
                        className="card border-0 shadow-sm cursor-pointer h-100"
                        style={{
                          cursor: isTyping ? 'not-allowed' : 'pointer', // Show not-allowed cursor when processing
                          transition: 'all 0.2s ease',
                          transform: 'scale(1)',
                          opacity: isTyping ? 0.6 : 1 // Dim while processing
                        }}
                        onClick={() => handlePopularQueryClick(query.query)} // Click to submit preset query
                        onMouseEnter={(e) => {
                          // Scale up on hover if not processing
                          if (!isTyping) {
                            e.currentTarget.style.transform = 'scale(1.02)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          // Reset scale on mouse leave
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
                        }}
                      >
                        <div className={`card-body ${isExpanded ? 'p-3' : 'p-2'}`}>
                          <div className="d-flex align-items-start">
                            {/* Query icon with gradient background */}
                            <div className="me-3" style={{ 
                              fontSize: isExpanded ? '2rem' : '1.5rem', 
                              background: 'linear-gradient(135deg, #0d6efd 60%, #6c63ff 100%)', 
                              borderRadius: '50%', 
                              width: isExpanded ? '40px' : '30px', 
                              height: isExpanded ? '40px' : '30px', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              color: '#fff',
                              transition: 'all 0.3s ease'
                            }}>
                              {query.icon}
                            </div>
                            
                            {/* Query title, description, and query preview */}
                            <div className="flex-grow-1 text-start">
                              <h6 className={`fw-bold mb-1 text-primary ${isExpanded ? '' : 'small'}`}>
                                {query.title}
                              </h6>
                                <>
                                  {/* Query description text */}
                                  <p className="text-muted small mb-2">{query.description}</p>
                                  {/* Preview of actual query to be sent */}
                                  <div className="text-primary small fw-medium" style={{ 
                                    background: '#e7f1ff', 
                                    borderRadius: '6px', 
                                    padding: '4px 8px', 
                                    display: 'inline-block', 
                                    fontStyle: 'italic' 
                                  }}>
                                    "{query.query}"
                                  </div>
                                </>
                             
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            // ========== MESSAGES DISPLAY AREA ========== 
            // Show message history when user has sent messages
            <div className="p-3">
              {/* Render all chat messages */}
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`mb-3 d-flex ${message.sender === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
                >
                  {/* Message bubble styling based on sender */}
                  <div
                    className={`p-3 rounded-3 max-w-75 ${
                      message.sender === 'user'
                        ? 'bg-primary text-white' // User messages: blue bubble on right
                        : 'bg-light text-dark border' // Assistant messages: light bubble on left
                    }`}
                    style={{ maxWidth: '75%' }}
                  >
                    {/* Render message content with markdown parsing for assistant messages */}
                    {message.sender === 'assistant' ? (
                      <MessageRenderer text={message.text} />
                    ) : (
                      // Plain text for user messages (no markdown needed)
                      message.text
                    )}
                  </div>
                </div>
              ))}
              
              {/* ========== TYPING INDICATOR ========== */}
              {/* Show animated dots when AI is processing response */}
              {isTyping && (
                <div className="mb-3 d-flex justify-content-start">
                  <div className="p-3 rounded-3 bg-light text-dark border">
                    {/* Three animated dots indicating typing */}
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Reference point for auto-scroll to latest message */}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* ========== MESSAGE INPUT AREA ========== */}
        {/* Sticky input section fixed at bottom of panel */}
        <div
  className="border-top p-3 bg-light"
  style={{
    position: 'sticky',
    bottom: 0,
    width: '100%',
    background: '#f8f9fa',
    zIndex: 1060 // Above panel content
  }}
>
  {/* Input group with textarea and send button */}
  <div className="input-group">
    {/* Message input textarea */}
    <textarea
      ref={inputRef}
      className="form-control"
      placeholder={isTyping ? "Please wait for response..." : "Type your message..."} // Dynamic placeholder based on processing state
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)} // Update input state on text change
      onKeyPress={handleKeyPress} // Submit on Enter key
      rows="1" // Single line by default, grows with content
      style={{ 
        resize: 'none', // Prevent manual resizing
        minHeight: '38px', // Minimum height for input
        opacity: isTyping ? 0.6 : 1, // Dim while processing
        cursor: isTyping ? 'not-allowed' : 'text' // Show disabled cursor while processing
      }}
      disabled={isTyping} // Disable input while AI is responding
    />
    
    {/* Send button */}
    <button
      className="btn btn-primary"
      onClick={() => handleSendMessage()} // Submit current message
      disabled={!inputValue.trim() || isTyping} // Disable if empty or processing
      style={{
        opacity: (!inputValue.trim() || isTyping) ? 0.6 : 1, // Dim when disabled
        cursor: (!inputValue.trim() || isTyping) ? 'not-allowed' : 'pointer' // Show appropriate cursor
      }}
    >
      {/* Send icon SVG */}
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
      </svg>
    </button>
  </div>
</div>

              </div>
      </>)}

              </>
              </div>
              </div>
              </div>

              {/* ========== CUSTOM COMPONENT STYLES ========== */}
              {/* Scoped CSS for chat panel animations and markdown styling */}
      <style jsx>{`
        /* Hover effect for chat button (if used) */
        .floating-chat-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.25) !important;
        }
        
        /* Slide-in animation for opening panel */
        .chat-panel-open {
          animation: ${isInitialized ? 'slideIn 0.3s ease-in-out' : 'none'};
        }
        
        /* Slide-out animation for closing panel */
        .chat-panel-closed {
          animation: ${isInitialized ? 'slideOut 0.3s ease-in-out' : 'none'};
        }
        
        /* ========== TYPING INDICATOR STYLES ========== */
        /* Container for typing indicator dots */
        .typing-indicator {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        /* Individual animated dots in typing indicator */
        .typing-indicator span {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #6c757d;
          animation: typing 1.4s infinite ease-in-out; /* Continuous typing animation */
        }
        
        /* Staggered animation delays for each dot */
        .typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
        .typing-indicator span:nth-child(2) { animation-delay: -0.16s; }
        .typing-indicator span:nth-child(3) { animation-delay: 0s; }
        
        /* ========== MARKDOWN RENDERING STYLES ========== */
        /* Numbered list items from parsed markdown */
        .numbered-item {
          margin: 2px 0;
          line-height: 1.4;
          padding: 2px 0;
          font-size: 14px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
          color: #212529;
        }
        
        /* Bullet list items from parsed markdown */
        .bullet-item {
          margin: 10px 2px 2px 10px !important;
          padding: 1px 0;
          line-height: 0.3;
          font-size: 14px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
          color: #212529;
        }
        
        /* Paragraph breaks from double line breaks */
        .paragraph-break {
          height: 12px;
        }
        
        /* ========== MESSAGE BUBBLE STYLES ========== */
        /* Assistant message bubble styling */
        .bg-light.text-dark.border {
          font-size: 14px !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif !important;
          line-height: 1.4 !important;
        }
        
        /* User message bubble styling */
        .bg-primary.text-white {
          font-size: 14px !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif !important;
          line-height: 1.4 !important;
        }
        
        /* ========== INLINE MARKDOWN FORMATTING ========== */
        /* Bold text styling from **text** markdown */
        strong {
          font-weight: 600 !important;
          color: #0d6efd !important;
        }
        
        /* Italic text styling from *text* markdown */
        em {
          font-style: italic;
          color: #6c757d;
        }
        
        /* Overall message container line height */
        .p-3.rounded-3 {
          line-height: 1.4 !important;
        }
        
        /* ========== KEYFRAME ANIMATIONS ========== */
        /* Typing indicator dot animation - scale up/down with opacity */
        @keyframes typing {
          0%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        /* Panel slide-in animation from right */
        @keyframes slideIn {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        
        /* Panel slide-out animation to right */
        @keyframes slideOut {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(100%);
          }
        }
        
        /* ========== MOBILE RESPONSIVE STYLES ========== */
        /* Adjust panel width and text sizes for mobile devices */
        @media (max-width: 767px) {
          /* Chat panel takes 85% of screen on mobile */
          #floating-chat-panel {
            width: 85% !important;
          }
          
          /* Chat panel takes 100% when expanded on mobile */
          #floating-chat-panel.expanded {
            width: 100% !important;
          }
          
          /* Reduce font size for lists on mobile */
          .numbered-item, .bullet-item {
            font-size: 13px;
          }
          
          /* Reduce font size for message bubbles on mobile */
          .bg-light.text-dark.border, .bg-primary.text-white {
            font-size: 13px !important;
          }
        }
      `}</style>

      {/* End of chat panel structure */}
    </>
  );
};

// Export component as default
export default AskGenie;
